import { useEffect, useMemo, useRef, useState } from 'react';
import { useDialogA11y } from '../../hooks/useDialogA11y';

export type CreateSpacePayload = {
  name: string;
  channels: { key: string; name: string; type: 'text' | 'voice' | 'announcement' | string }[];
  homeKey: string;
};

type SpaceSearchResult = {
  id: string;
  name?: string;
  avatarUrl?: string | null;
  description?: string | null;
  requestStatus?: string | null;
};

export default function CreateSpaceModal({
  open,
  onCancel,
  onSubmit,
  onJoin,
  onSearchSpaces,
  onRequestJoin,
}: {
  open: boolean;
  onCancel: () => void;
  onSubmit: (payload: CreateSpacePayload) => void | Promise<void>;
  onJoin: (code: string) => Promise<void>;
  onSearchSpaces: (query: string) => Promise<SpaceSearchResult[]>;
  onRequestJoin: (spaceId: string) => Promise<{ status: string } | string>;
}) {
  const [spaceName, setSpaceName] = useState('');
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'create' | 'join'>('create');
  const [joinCode, setJoinCode] = useState('');
  const [joinBusy, setJoinBusy] = useState(false);
  const [joinErr, setJoinErr] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchErr, setSearchErr] = useState('');
  const [searchResults, setSearchResults] = useState<SpaceSearchResult[]>([]);
  const [searchTouched, setSearchTouched] = useState(false);
  const [requestingId, setRequestingId] = useState<string | null>(null);

  const templates = useMemo(() => ([
    { key: 'general', label: 'General', type: 'text' as const, defaultOn: true },
    { key: 'voice', label: 'Voice', type: 'voice' as const, defaultOn: false },
    { key: 'tasks', label: 'Tasks', type: 'kanban' as any, defaultOn: false },
    { key: 'forms', label: 'Questions', type: 'form' as any, defaultOn: false },
    { key: 'habits', label: 'Task tracker', type: 'habit' as any, defaultOn: false },
    { key: 'gallery', label: 'Gallery', type: 'gallery' as any, defaultOn: false },
    { key: 'notes', label: 'Notes', type: 'notes' as any, defaultOn: false },
  ]), []);

  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [homeKey, setHomeKey] = useState('general');

  useEffect(() => {
    if (open) {
      setSpaceName('');
      setMode('create');
      setJoinCode('');
      setJoinErr('');
      setSearchQuery('');
      setSearchErr('');
      setSearchResults([]);
      setSearchTouched(false);
      setRequestingId(null);
      setSearchBusy(false);
      setBusy(false);
      setJoinBusy(false);
      const init: Record<string, boolean> = {};
      for (const t of templates) init[t.key] = !!t.defaultOn;
      setSelected(init);
      setHomeKey('general');
    }
  }, [open, templates]);

  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(open, dialogRef, () => { if (!(busy || joinBusy)) onCancel(); });

  if (!open) return null;

  const selectedList = templates.filter(t => selected[t.key]);
  const canSubmit = spaceName.trim().length > 0 && selectedList.length > 0 && !!selected[homeKey];
  const canJoin = joinCode.trim().length > 0 && !joinBusy;
  const canSearch = searchQuery.trim().length >= 2 && !searchBusy;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={() => !(busy || joinBusy) && onCancel()} />
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="create-space-title" tabIndex={-1} className="relative w-full max-w-lg rounded-2xl border border-neutral-800 bg-neutral-900 p-4 md:p-5 shadow-2xl outline-none">
        <div id="create-space-title" className="text-fg text-lg font-medium mb-3">Add Space</div>
        <div className="mb-4 flex gap-2">
          <button
            className={`flex-1 rounded-full px-3 py-1.5 text-sm font-medium ${mode === 'create' ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40' : 'border border-neutral-800 text-fg-2 hover:text-fg'}`}
            onClick={() => !joinBusy && setMode('create')}
            disabled={joinBusy}
          >
            Create new
          </button>
          <button
            className={`flex-1 rounded-full px-3 py-1.5 text-sm font-medium ${mode === 'join' ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40' : 'border border-neutral-800 text-fg-2 hover:text-fg'}`}
            onClick={() => !busy && setMode('join')}
            disabled={busy}
          >
            Join Space
          </button>
        </div>

        {mode === 'create' ? (
          <>
            <div className="mb-4">
              <div className="text-xs text-fg-2 mb-1">Space name</div>
              <input
                className="w-full h-10 px-3 rounded-md bg-neutral-950 text-fg placeholder-neutral-500 border border-neutral-800 focus:outline-none accent-ring focus:ring-2"
                placeholder="My Team"
                aria-label="Space name"
                value={spaceName}
                onChange={(e) => setSpaceName(e.target.value)}
                disabled={busy}
              />
            </div>

            <div className="mb-4">
              <div className="text-xs text-fg-2 mb-2">Start with these channels</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {templates.map(t => (
                  <label key={t.key} className="flex items-center gap-2 p-2 rounded-md border border-neutral-800 bg-neutral-900/60 hover:bg-neutral-900/80">
                    <input
                      type="checkbox"
                      className="accent-ring"
                      checked={!!selected[t.key]}
                      onChange={(e) => {
                        const next = { ...selected, [t.key]: e.target.checked };
                        if (Object.values(next).some(Boolean)) {
                          setSelected(next);
                          if (!next[homeKey]) {
                            const first = templates.find(x => next[x.key]);
                            if (first) setHomeKey(first.key);
                          }
                        }
                      }}
                      disabled={busy}
                    />
                    <div className="flex-1">
                      <div className="text-fg text-sm">{t.label}</div>
                      <div className="text-meta text-fg-3">{t.type}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-2">
              <div className="text-xs text-fg-2 mb-1">Default (Home) channel</div>
              <select
                className="w-full h-10 px-3 rounded-md bg-neutral-950 text-fg border border-neutral-800 focus:outline-none accent-ring focus:ring-2"
                value={homeKey}
                onChange={(e) => setHomeKey(e.target.value)}
                disabled={busy}
              >
                {selectedList.map(t => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
              <div className="mt-1 text-meta text-fg-3">Members land here when they open the space.</div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button className="px-3 py-2 rounded border border-neutral-700 text-fg hover:bg-neutral-800/70" onClick={onCancel} disabled={busy}>Cancel</button>
              <button
                className="px-3 py-2 rounded border border-emerald-700 bg-emerald-800/70 text-emerald-50 hover:bg-emerald-700/70 disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={async () => {
                  if (!canSubmit || busy) return;
                  setBusy(true);
                  try {
                    const payload: CreateSpacePayload = {
                      name: spaceName.trim(),
                      channels: selectedList.map(t => ({ key: t.key, name: t.label.toLowerCase(), type: t.type })),
                      homeKey,
                    };
                    await Promise.resolve(onSubmit(payload));
                  } finally {
                    setBusy(false);
                  }
                }}
                disabled={!canSubmit}
              >
                Create Space
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-4">
              <div className="text-xs text-fg-2 mb-1">Invite code</div>
              <input
                className="w-full h-10 px-3 rounded-md bg-neutral-950 text-fg placeholder-neutral-500 border border-neutral-800 focus:outline-none accent-ring focus:ring-2"
                placeholder="TEAM-ALPHA"
                aria-label="Invite code"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                disabled={joinBusy}
              />
            </div>
            {joinErr && <div className="mb-3 text-sm text-red-400">{joinErr}</div>}
            <div className="my-5 border-t border-neutral-800" />
            <div className="mb-2 text-sm font-medium text-fg">Search spaces</div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                className="w-full h-10 px-3 rounded-md bg-neutral-950 text-fg placeholder-neutral-500 border border-neutral-800 focus:outline-none accent-ring focus:ring-2"
                placeholder="Search by space name"
                aria-label="Search by space name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={searchBusy}
              />
              <button
                className="px-3 py-2 rounded border border-neutral-700 text-fg hover:bg-neutral-800/70 disabled:opacity-60"
                onClick={async () => {
                  if (!canSearch) return;
                  setSearchBusy(true);
                  setSearchTouched(true);
                  setSearchErr('');
                  try {
                    const res = await onSearchSpaces(searchQuery.trim());
                    setSearchResults(Array.isArray(res) ? res : []);
                  } catch (e: any) {
                    setSearchErr(e?.message || 'Search failed');
                  } finally {
                    setSearchBusy(false);
                  }
                }}
                disabled={!canSearch}
              >
                {searchBusy ? 'Searching...' : 'Search'}
              </button>
            </div>
            {searchErr && <div className="mt-2 text-sm text-red-400">{searchErr}</div>}
            {searchTouched && !searchBusy && searchResults.length === 0 && !searchErr && (
              <div className="mt-2 text-xs text-fg-3">No spaces found.</div>
            )}
            {searchResults.length > 0 && (
              <div className="mt-3 space-y-2 max-h-64 overflow-auto pr-1">
                {searchResults.map((s) => {
                  const status = String(s.requestStatus || '').toLowerCase();
                  const isPending = status === 'pending';
                  const isRejected = status === 'rejected';
                  return (
                    <div key={s.id} className="flex items-start gap-3 rounded-lg border border-neutral-800 bg-neutral-950/60 p-3">
                      <div className="h-9 w-9 rounded-full overflow-hidden border border-neutral-700 bg-neutral-800 flex items-center justify-center">
                        {s.avatarUrl ? (
                          <img src={s.avatarUrl} alt={s.name || 'space'} className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-xs text-fg-3">{(s.name || '?').slice(0,1).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-fg font-medium truncate">{s.name || 'Untitled space'}</div>
                        {s.description && <div className="text-xs text-fg-2 break-words">{s.description}</div>}
                        {isPending && <div className="mt-1 text-meta text-amber-300">Pending</div>}
                        {isRejected && <div className="mt-1 text-meta text-red-300">Not approved</div>}
                      </div>
                      <div className="flex items-center">
                        <button
                          className="px-2.5 py-1.5 rounded border border-emerald-700 bg-emerald-800/70 text-emerald-50 hover:bg-emerald-700/70 disabled:opacity-60"
                          disabled={requestingId === s.id || isPending}
                          onClick={async () => {
                            setRequestingId(s.id);
                            try {
                              const res = await onRequestJoin(s.id);
                              const nextStatus = typeof res === 'string' ? res : (res as any)?.status;
                              setSearchResults((prev) => prev.map((r) => r.id === s.id ? { ...r, requestStatus: nextStatus || 'pending' } : r));
                            } catch (e: any) {
                              setSearchErr(e?.message || 'Request failed');
                            } finally {
                              setRequestingId(null);
                            }
                          }}
                        >
                          {isRejected ? 'Request again' : 'Request'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button className="px-3 py-2 rounded border border-neutral-700 text-fg hover:bg-neutral-800/70" onClick={onCancel} disabled={joinBusy}>Cancel</button>
              <button
                className="px-3 py-2 rounded border border-emerald-700 bg-emerald-800/70 text-emerald-50 hover:bg-emerald-700/70 disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={async () => {
                  if (!canJoin) return;
                  setJoinBusy(true);
                  setJoinErr('');
                  try {
                    await onJoin(joinCode.trim());
                    onCancel();
                  } catch (e: any) {
                    setJoinErr(e?.message || 'Failed to join space');
                  } finally {
                    setJoinBusy(false);
                  }
                }}
                disabled={!canJoin}
              >
                Join Space
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
